import { RuntimePropInstance } from '../PropMechanicsTypes';
import { RuntimeNodeBase } from './RuntimeNodeBase';
import { getRuntimeYield, uniqueLiveItems } from './RuntimeNodeUtils';

/**
 * 整数常量节点。
 *
 * 输出 data.value 对应的整数。
 */
export class IntConstantNode extends RuntimeNodeBase {
    /**
     * 输出整数值端口。
     *
     * @returns 向下取整后的常量值。
     */
    public value(): number {
        return Math.floor(Number(this.node.data?.value ?? 0));
    }
}

/**
 * 浮点常量节点。
 *
 * 输出 data.value 对应的浮点数。
 */
export class FloatConstantNode extends RuntimeNodeBase {
    /**
     * 输出浮点值端口。
     *
     * @returns 常量浮点值。
     */
    public value(): number {
        return Number(this.node.data?.value ?? 0);
    }
}

/**
 * 品质常量节点。
 *
 * 输出品质枚举值。
 */
export class RarityConstantNode extends RuntimeNodeBase {
    /**
     * 输出品质值端口。
     *
     * @returns 品质枚举数字。
     */
    public value(): number {
        return Number(this.node.data?.value ?? 0);
    }
}

/**
 * 标签常量节点。
 *
 * 输出标签枚举值。
 */
export class TagConstantNode extends RuntimeNodeBase {
    /**
     * 输出标签值端口。
     *
     * @returns 标签枚举数字。
     */
    public value(): number {
        return Number(this.node.data?.value ?? 0);
    }
}

/**
 * 整数属性节点。
 *
 * 从目标实例读取指定属性，并以整数形式输出。
 */
export class IntAttributeNode extends RuntimeNodeBase {
    /**
     * 输出整数属性值端口。
     *
     * @returns 目标属性的整数值。
     */
    public value(): number {
        return Math.floor(readTargetAttributeValue(this.node.data, this.inputTargets('target'), this.context.currentTarget, this.context.owner));
    }
}

/**
 * 浮点属性节点。
 *
 * 从目标实例读取指定属性，并以浮点数形式输出。
 */
export class FloatAttributeNode extends RuntimeNodeBase {
    /**
     * 输出浮点属性值端口。
     *
     * @returns 目标属性的浮点值。
     */
    public value(): number {
        return readTargetAttributeValue(this.node.data, this.inputTargets('target'), this.context.currentTarget, this.context.owner);
    }
}

/**
 * 数量节点。
 *
 * 输出目标数组中的实例数量。
 */
export class IntQuantityNode extends RuntimeNodeBase {
    /**
     * 输出数量端口。
     *
     * @returns 上游目标数量。
     */
    public value(): number {
        return this.inputTargets('targets').length;
    }
}

/**
 * 种类节点。
 *
 * 按道具原型 `_id` 统计目标数组中的不同种类数。
 */
export class IntKindCountNode extends RuntimeNodeBase {
    /**
     * 输出种类数量端口。
     *
     * @returns 不同原型 id 的数量。
     */
    public value(): number {
        return new Set(this.inputTargets('targets').map((item) => item.graph._id)).size;
    }
}

/**
 * 整数随机节点。
 *
 * 在 `[min, max]` 闭区间内输出随机整数。
 */
export class IntRandomNode extends RuntimeNodeBase {
    /**
     * 输出随机整数端口。
     *
     * @returns 闭区间随机整数；若 max 小于 min，会自动交换范围。
     */
    public value(): number {
        const min = Math.floor(this.inputNumber('min', 0));
        const max = Math.floor(this.inputNumber('max', min));
        if (max < min) return this.context.rng.int(max, min + 1);
        return this.context.rng.int(min, max + 1);
    }
}

/**
 * 浮点随机节点。
 *
 * 在 `[min, max)` 区间内输出随机浮点数。
 */
export class FloatRandomNode extends RuntimeNodeBase {
    /**
     * 输出随机浮点数端口。
     *
     * @returns 区间随机浮点数；若 max 小于 min，会自动交换范围。
     */
    public value(): number {
        const min = this.inputNumber('min', 0);
        const max = this.inputNumber('max', min);
        if (max < min) return this.context.rng.float(max, min);
        return this.context.rng.float(min, max);
    }
}

function readTargetAttributeValue(
    data: Record<string, unknown> | undefined,
    inputTargets: RuntimePropInstance[],
    currentTarget: RuntimePropInstance | undefined,
    owner: RuntimePropInstance,
): number {
    const targets = uniqueLiveItems([
        ...inputTargets,
        currentTarget,
        owner,
    ].filter(Boolean) as RuntimePropInstance[]);
    const target = targets[0];
    if (!target) return 0;

    const attribute = String(data?.attribute ?? data?.field ?? '');
    if (attribute === 'rarity') return Number(target.graph.rarity || 0);
    if (attribute === 'tag') return Number(target.graph.tags[0] || 0);
    if (attribute === 'yield') return getRuntimeYield(target);
    if (attribute === 'type') return Number(target.graph.type || 0);
    if (attribute === 'quantity') return 1;
    return 0;
}
